import {
	FLAG_ENABLE_TIMERS,
	GAME_TICKER_ENABLED,
	GAME_TICKER_INTERVAL,
} from "@/config/constants";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useQuestStore from "@/store/useQuestStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import GameError, { GameErrorType } from "./GameError";

export enum GameTickerLockReason {
	OFFLINE_INCOME = "offline-income",
	EVENT_START = "event-start",
}

type HandlerKey = `${number}:${string}`;

type HandlerFunction = () => void | Promise<void>;

class GameTicker {
	private readonly emitter: ReturnType<typeof useEmitter>;

	// TODO: kluce by mali zacinat cislom podla priority
	private readonly handlers = new Map<HandlerKey, HandlerFunction>();

	private timeoutId: ReturnType<typeof setTimeout> | null = null;

	private lastTick: number = 0;

	private savedElapsedTime: number = 0;

	private lock: GameTickerLockReason | null = null;

	public constructor() {
		this.emitter = useEmitter();
	}

	public get isStarted(): boolean {
		return this.timeoutId !== null;
	}

	public get isLocked(): boolean {
		return this.lock !== null;
	}

	public get lockReason(): GameTickerLockReason | null {
		return this.lock;
	}

	public get progress(): number {
		if (!this.lastTick) return 0;

		let progress = +(
			(Date.now() - this.lastTick) /
			GAME_TICKER_INTERVAL
		).toFixed(4);
		progress = Math.min(progress, 1);
		progress = Math.max(progress, 0);

		return progress;
	}

	public start(lockReason: GameTickerLockReason | null = null): void {
		if (!GAME_TICKER_ENABLED) return;
		if (this.lock && this.lock !== lockReason)
			throw new GameError(
				GameErrorType.FE_ERROR,
				`[start] GameTicker is already locked by reason: ${this.lock}`,
			);
		if (useQuestStore().tutorialActive) return;
		if (useRuntimeStore().isInactivityPopupVisible) return;
		if (this.isStarted) return;

		this.lastTick = Date.now();

		const callback = async (): Promise<void> => {
			this.lastTick = Date.now();

			await this.tick();
			if (this.timeoutId) clearTimeout(this.timeoutId);
			if (!this.isStarted || useRuntimeStore().isInactivityPopupVisible)
				return;
			this.timeoutId = setTimeout(callback, GAME_TICKER_INTERVAL);
		};

		if (this.timeoutId) clearTimeout(this.timeoutId);
		this.timeoutId = setTimeout(
			callback,
			GAME_TICKER_INTERVAL - this.savedElapsedTime,
		);

		this.lock = null;
	}

	public stop(lockReason: GameTickerLockReason | null = null): void {
		if (!this.isStarted) return;

		this.lastTick = 0;

		if (this.timeoutId) clearTimeout(this.timeoutId);
		this.timeoutId = null;

		this.lock = lockReason;
	}

	public resume(lockReason: GameTickerLockReason | null = null): void {
		if (!GAME_TICKER_ENABLED) return;
		if (this.lock && this.lock !== lockReason)
			throw new GameError(
				GameErrorType.FE_ERROR,
				`[start] GameTicker is already locked by reason: ${this.lock}`,
			);
		if (useRuntimeStore().isInactivityPopupVisible) return;
		if (this.isStarted) return;

		this.start();

		this.lastTick = Date.now() - this.savedElapsedTime;
		this.savedElapsedTime = 0;

		this.lock = null;
	}

	public pause(lockReason: GameTickerLockReason | null = null): void {
		if (!this.isStarted) return;

		this.savedElapsedTime = Date.now() - this.lastTick;

		this.stop();

		this.lock = lockReason;
	}

	public addHandler(key: HandlerKey, handler: HandlerFunction): void {
		this.handlers.set(key, handler);
	}

	public removeHandler(key: HandlerKey): void {
		this.handlers.delete(key);
	}

	private async tick(): Promise<void> {
		const entries = Array.from(this.handlers.entries()).sort(
			(
				[a]: [HandlerKey, HandlerFunction],
				[b]: [HandlerKey, HandlerFunction],
			): number => {
				const [aNumStr] = a.split(":");
				const [bNumStr] = b.split(":");
				const aNum = +aNumStr;
				const bNum = +bNumStr;
				return aNum - bNum;
			},
		);
		for (const [name, handler] of entries) {
			if (FLAG_ENABLE_TIMERS) {
				// eslint-disable-next-line no-console
				console.time(`GameTicker Handler: ${name}`);
			}

			await Promise.resolve(handler());

			if (FLAG_ENABLE_TIMERS) {
				// eslint-disable-next-line no-console
				console.timeEnd(`GameTicker Handler: ${name}`);
			}
		}

		SoundService.getInstance().playTickIncome();

		this.emitter.emit(EmitterEvents.GameTick);
	}
}

export default GameTicker;
